import React, { useState } from "react";
import { toast } from "sonner";
import { X, KeyRound, Eye, EyeOff } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";

/**
 * Phase 3.5D/E — Reauthentication dialog.
 *
 * When the server returns 401 { detail: { code: "reauth_required" } } the
 * caller opens this dialog. Any provider that appears in `methods` (server-
 * side `detail.available`) is offered. Password reauth is inline; Google/
 * Apple reauth kicks off a full-page redirect through
 * `/api/auth/oauth/{p}/start?mode=reauth`; the callback bumps
 * `sessions.recent_auth_at` server-side and redirects back to `return_to`.
 */
export default function ReauthDialog({
  open,
  onClose,
  onSuccess,
  methods = ["password"],
  title = "Confirm your identity",
  copy = "For your security, please confirm your identity to continue.",
  returnTo,
}) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/security/reauth/password", { password });
      toast.success("Identity confirmed.");
      setPassword("");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const providerReauth = (provider) => {
    const back = returnTo || (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/dashboard?tab=security");
    const url = `${API}/auth/oauth/${provider}/start?mode=reauth&return_to=${encodeURIComponent(back)}`;
    window.location.href = url;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-title"
      data-testid="reauth-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
    >
      <div className="w-full max-w-md rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[10px_10px_0_var(--panini-red)]"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="kicker kicker-red">Security check</div>
            <h2 id="reauth-title" className="font-display text-2xl mt-2">{title}</h2>
            <p className="mt-2 text-sm text-[var(--smoke)]">{copy}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="reauth-close"
            aria-label="Close"
            className="text-[var(--smoke)] hover:text-[var(--ink)]"
          >
            <X size={20} />
          </button>
        </div>

        {methods.includes("google") && (
          <button
            type="button"
            onClick={() => providerReauth("google")}
            data-testid="reauth-google"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-xl border-2 border-[var(--ink)] bg-white px-4 py-3 text-sm font-bold text-[var(--ink)] shadow-[3px_3px_0_var(--ink)] hover:shadow-[5px_5px_0_var(--ink)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
          >
            Continue with Google
          </button>
        )}
        {methods.includes("apple") && (
          <button
            type="button"
            onClick={() => providerReauth("apple")}
            data-testid="reauth-apple"
            className="mt-3 w-full inline-flex items-center justify-center gap-3 rounded-xl border-2 border-[var(--ink)] bg-[var(--ink)] px-4 py-3 text-sm font-bold text-white shadow-[3px_3px_0_var(--panini-yellow)] hover:shadow-[5px_5px_0_var(--panini-yellow)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition"
          >
            Continue with Apple
          </button>
        )}

        {methods.includes("password") ? (
          <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="reauth-password-form">
            <label className="block">
              <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">
                Password
              </div>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="reauth-password"
                  className="gm-field pr-11"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] hover:text-[var(--ink)]"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="reauth-submit"
              className="btn-red w-full justify-center text-sm disabled:opacity-60"
            >
              <KeyRound size={16} /> {busy ? "Verifying…" : "Confirm identity"}
            </button>
          </form>
        ) : (
          !methods.includes("google") && !methods.includes("apple") && (
            <div className="mt-6 text-sm text-[var(--smoke)]" data-testid="reauth-no-method">
              No available reauthentication method for this account. Please sign
              out and back in with your provider.
            </div>
          )
        )}
      </div>
    </div>
  );
}

export function reauthMethodsFromError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && Array.isArray(detail.available)) {
    return detail.available.length ? detail.available : ["password"];
  }
  return ["password"];
}

export function isReauthError(err) {
  const detail = err?.response?.data?.detail;
  return (
    err?.response?.status === 401 &&
    detail && typeof detail === "object" && detail.code === "reauth_required"
  );
}
